/* @flow */

import React from 'react';

import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';

import ButtonComponent from 'components/buttons/button';
import IntlMessageComponent from 'components/formatters/intlMessage';

type TwoFactorConfirmDialogComponentProps = {
  open: boolean;
  qrActivated: boolean;
  smsActivated: boolean;
  onSendSms: (qrpin: string) => Promise<any>;
  onConfirm: (codes: { twoFactorPin?: string, smsCode?: string }) => Promise<any>;
  onCancel: () => void;
}

export default function TwoFactorConfirmDialogComponent(props: TwoFactorConfirmDialogComponentProps) {
  const [step, setStep] = React.useState('qr');
  const [qrPin, setQrPin] = React.useState('');
  const [smsCode, setSmsCode] = React.useState('');
  const [wrongCode, setWrongCode] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const smsAutoSent = React.useRef(false);

  React.useEffect(() => {
    if (!props.open) {
      smsAutoSent.current = false;
      return;
    }

    setQrPin('');
    setSmsCode('');
    setWrongCode(false);

    const smsOnly = props.smsActivated && !props.qrActivated;
    setStep(smsOnly ? 'sms' : 'qr');

    if (smsOnly && !smsAutoSent.current) {
      smsAutoSent.current = true;
      props.onSendSms('').catch(() => setWrongCode(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const handleQrPinChange = (value: string) => {
    setQrPin(value);
    setWrongCode(false);
  };

  const handleSmsCodeChange = (value: string) => {
    setSmsCode(value);
    setWrongCode(false);
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      if (step === 'qr' && props.smsActivated) {
        await props.onSendSms(qrPin);
        setStep('sms');
        setWrongCode(false);
      } else if (step === 'qr') {
        await props.onConfirm({ twoFactorPin: qrPin });
      } else {
        // A dual-method user must prove both factors: carry the authenticator PIN entered on the
        // first step through to the final call alongside the SMS code.
        const codes = props.qrActivated ? { twoFactorPin: qrPin, smsCode } : { smsCode };
        await props.onConfirm(codes);
      }
    } catch (e) {
      setWrongCode(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onCancel} fullWidth maxWidth="sm">
      <DialogTitle><IntlMessageComponent id="twofactor.title" /></DialogTitle>
      <DialogContent>
        <DialogContentText><IntlMessageComponent id="twofactor.passwordchange.intro" /></DialogContentText>

        {step === 'qr' ? (
          <TextField
            id="twofactor.passwordchange.qrpin"
            label={<IntlMessageComponent id="appModule.twofactor.qrlabel" />}
            value={qrPin}
            onChange={(e) => handleQrPinChange(e.target.value)}
            margin="normal"
            type="password"
            fullWidth
            required
          />
        ) : (
          <TextField
            id="twofactor.passwordchange.smscode"
            label={<IntlMessageComponent id="appModule.twofactor.smslabel" />}
            value={smsCode}
            onChange={(e) => handleSmsCodeChange(e.target.value)}
            margin="normal"
            type="password"
            fullWidth
            required
          />
        )}

        {wrongCode &&
          <Box mt={1} color="error.main"><IntlMessageComponent id="appModule.signInFailed.invalid2fa" /></Box>}
      </DialogContent>
      <DialogActions>
        <ButtonComponent
          color="default"
          variant="contained"
          onClick={props.onCancel}
        ><IntlMessageComponent id="general.cancel" /></ButtonComponent>
        <ButtonComponent
          color="primary"
          variant="contained"
          isProcessing={isProcessing}
          onClick={handleConfirm}
        ><IntlMessageComponent id="twofactor.passwordchange.confirm" /></ButtonComponent>
      </DialogActions>
    </Dialog>
  );
}
